// SkipInventoryEditPage — /skip/inventory/:id/edit. Staff-accessible.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import {
  useSkipInventoryItem,
  useUpdateSkipInventoryItem,
} from '../hooks/useSkipInventory';
import {
  useSkipStockSizes,
  useSkipStockTypes,
} from '../settings/hooks/useSkipStock';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipInventoryEditPage.module.css';

interface FormState {
  skip_number: string;
  skip_size_id: string;
  stock_type_enc_id: string;
  skip_condition: 'excellent' | 'good' | 'fair' | 'poor' | 'repair_required';
  current_location: string;
  last_service_date: string;
  next_service_date: string;
  notes: string;
}

export function SkipInventoryEditPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemQuery = useSkipInventoryItem(encId);
  const update = useUpdateSkipInventoryItem(encId);

  // forSelection: true → server hides globals the tenant hasn't
  // activated. The detail page (read view) renders the saved type's
  // name from the inventory record itself, so a previously-activated-
  // then-deactivated global on a saved unit still displays correctly.
  const typesQuery = useSkipStockTypes({ forSelection: true });
  const stockTypes = typesQuery.data?.stockTypes ?? [];

  // Standard (global) vs Custom (own-tenant) groups for the dropdown.
  // Labels only render when both groups exist so single-section
  // tenants stay clean.
  const standardTypes = useMemo(
    () => stockTypes.filter((t) => t.is_global),
    [stockTypes],
  );
  const customTypes = useMemo(
    () => stockTypes.filter((t) => !t.is_global),
    [stockTypes],
  );
  const showGroupLabels = standardTypes.length > 0 && customTypes.length > 0;

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const item = itemQuery.data?.item;
    if (!item || form !== null) return;
    setForm({
      skip_number: item.skip_number,
      skip_size_id: item.skip_size_enc_id ?? '',
      stock_type_enc_id: item.skip_stock_type_enc_id ?? '',
      skip_condition: item.skip_condition,
      current_location: item.current_location ?? '',
      last_service_date: item.last_service_date ?? '',
      next_service_date: item.next_service_date ?? '',
      notes: item.notes ?? '',
    });
  }, [itemQuery.data, form]);

  const sizesQuery = useSkipStockSizes(form?.stock_type_enc_id || null);
  const sizes = sizesQuery.data?.sizes ?? [];

  if (itemQuery.isLoading || form === null) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading…" />
      </div>
    );
  }

  if (itemQuery.isError || !itemQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Inventory unit not found" />
        <Card>
          <p>This unit could not be loaded.</p>
        </Card>
      </div>
    );
  }

  const item = itemQuery.data.item;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form === null) return;
    setError(null);
    const body: Record<string, unknown> = {
      skip_number: form.skip_number,
      skip_condition: form.skip_condition,
      current_location: form.current_location || null,
      notes: form.notes || null,
      last_service_date: form.last_service_date || null,
      next_service_date: form.next_service_date || null,
    };
    if (form.skip_size_id) body['skip_size_id'] = form.skip_size_id;
    update.mutate(body, {
      onSuccess: () => navigate(`/skip/inventory/${encodeURIComponent(encId)}`),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit ${item.skip_number}`}
        subtitle="Update inventory unit details"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate(`/skip/inventory/${encodeURIComponent(encId)}`)}
          >
            Cancel
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className={styles.form}>
        <Card>
          <h2 className={styles.sectionHeading}>Unit Details</h2>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Stock Type</span>
              <select
                value={form.stock_type_enc_id}
                onChange={(e) =>
                  setForm({ ...form, stock_type_enc_id: e.target.value, skip_size_id: '' })
                }
                aria-label="Stock Type"
              >
                <option value="">— Select —</option>
                {showGroupLabels ? (
                  <>
                    <optgroup label="── Standard ──">
                      {standardTypes.map((t) => (
                        <option key={t.enc_id} value={t.enc_id}>
                          {t.name} [{t.prefix}]
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="── Custom ──">
                      {customTypes.map((t) => (
                        <option key={t.enc_id} value={t.enc_id}>
                          {t.name} [{t.prefix}]
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  stockTypes.map((t) => (
                    <option key={t.enc_id} value={t.enc_id}>
                      {t.name} [{t.prefix}]
                    </option>
                  ))
                )}
              </select>
            </label>
            <label className={styles.field}>
              <span>Size</span>
              <select
                disabled={!form.stock_type_enc_id}
                value={form.skip_size_id}
                onChange={(e) => setForm({ ...form, skip_size_id: e.target.value })}
                aria-label="Size"
              >
                <option value="">
                  {form.stock_type_enc_id ? '— Select —' : 'Select stock type first'}
                </option>
                {sizes.map((s) => (
                  <option key={s.enc_id} value={s.enc_id}>
                    {s.name}
                    {s.typical_capacity ? ` (${s.typical_capacity})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Unit Number</span>
              <input
                required
                value={form.skip_number}
                onChange={(e) => setForm({ ...form, skip_number: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Condition</span>
              <select
                value={form.skip_condition}
                onChange={(e) =>
                  setForm({
                    ...form,
                    skip_condition: e.target.value as FormState['skip_condition'],
                  })
                }
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="repair_required">Repair required</option>
              </select>
            </label>
            <label className={styles.field}>
              <span>Current Location</span>
              <input
                value={form.current_location}
                onChange={(e) => setForm({ ...form, current_location: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Last Service</span>
              <input
                type="date"
                value={form.last_service_date}
                onChange={(e) => setForm({ ...form, last_service_date: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Next Service</span>
              <input
                type="date"
                value={form.next_service_date}
                onChange={(e) => setForm({ ...form, next_service_date: e.target.value })}
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>Notes</span>
            <textarea
              rows={3}
              maxLength={5000}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/skip/inventory/${encodeURIComponent(encId)}`)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
